import { type FC, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'
import { Button } from '../../components/Button/Button'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { type RoleGroup } from '../../models/Role'

const SEARCH_DEBOUNCE_MS = 400

export interface TeamListFiltersProps {
  namePattern: string | null
  roleGroupNames: string[]
  roleGroupOptions: RoleGroup[]
  hasActiveFilters: boolean
  onNamePatternChange: (value: string | null) => void
  onRoleGroupNamesChange: (names: string[]) => void
  onClearFilters: () => void
}

export const TeamListFilters: FC<TeamListFiltersProps> = ({
  namePattern,
  roleGroupNames,
  roleGroupOptions,
  hasActiveFilters,
  onNamePatternChange,
  onRoleGroupNamesChange,
  onClearFilters,
}) => {
  const [nameInput, setNameInput] = useState(namePattern ?? '')
  const [lastSeenNamePattern, setLastSeenNamePattern] = useState(namePattern)

  if (namePattern !== lastSeenNamePattern) {
    setLastSeenNamePattern(namePattern)
    setNameInput(namePattern ?? '')
  }

  const writeNamePattern = useDebouncedCallback((value: string) => {
    onNamePatternChange(value.trim() === '' ? null : value)
  }, SEARCH_DEBOUNCE_MS)

  const handleNameInputChange = (value: string) => {
    setNameInput(value)
    writeNamePattern(value)
  }

  const handleClearAll = () => {
    writeNamePattern.cancel()
    setNameInput('')
    onClearFilters()
  }

  return (
    <Row className='g-3 align-items-center justify-content-end'>
      <Col xs='auto'>
        {hasActiveFilters ? (
          <Button className='d-flex gap-1 align-items-center button-small p-0' onClick={handleClearAll}>
            <i className='bi bi-arrow-clockwise' />
            <span className='lead-small'>Clear all</span>
          </Button>
        ) : null}
      </Col>
      <Col xs={12} sm={5} lg={4} xl={3}>
        <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-400 zocks-px-0.75'>
          <i className='bi bi-search zocks-text-gray-500' />
          <Form.Control
            type='search'
            autoComplete='off'
            value={nameInput}
            onChange={(event) => handleNameInputChange(event.currentTarget.value)}
            placeholder='Search name'
            aria-label='Search name'
            className='border-0 px-0 paragraph-small'
          />
        </div>
      </Col>
      <Col xs={12} sm={4} lg={3} xl={2}>
        <MultiSelectDropdown
          name='roles'
          placeholder='Role'
          options={roleGroupOptions.map((group) => ({ id: group.name, label: group.name }))}
          initialSelectedOptions={roleGroupNames}
          onSelectedChanged={onRoleGroupNamesChange}
          changeOnClose
        />
      </Col>
    </Row>
  )
}
